import {
  Box,
  Button,
  Divider,
  Typography,
  SxProps,
  Theme
} from '@mui/material';
import React from 'react';
import { Card } from 'src/components/UI/Card';
import { CitationCampaignSx } from 'src/content/CitationBuilderV2/styles/style';
import {
  headerContainerSx,
  titleSx,
  headerActionsSx,
  totalPriceSx,
  descriptionSx,
  removeAllButtonSx
} from '../styles/createCampaign';
import { useCitation } from '../Providers/CitationProvider';

const FEATURES = [
  'Keep your business details updated and synced across Google, Apple, Facebook, effortlessly.',
  'Shield your listings on these four major platforms from unauthorized changes.',
  'Schedule and publish impactful posts to your Google Business Profile in less time.'
] as const;

const TAGS = ['Active Sync', 'GBP Post Scheduler'] as const;

const CHECKMARK_ICON_PATH =
  '/static/images/icons/local-seo/checkmark-circle-blue.svg';

// Component Styles
const containerSx: SxProps<Theme> = {
  paddingX: { xs: '16px', sm: '24px' }
};

const tagsContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'row',
  gap: { xs: '8px', sm: '12px' },
  marginTop: { xs: '16px' }
};

const tagSx: SxProps<Theme> = {
  display: 'inline-block',
  backgroundColor: 'var(--color-bg-success-light)',
  color: 'var(--color-success-deep)',
  paddingX: '16px',
  paddingY: '8px',
  borderRadius: '24px',
  fontFamily: 'Inter',
  fontWeight: 500,
  fontSize: { xs: '12px', sm: '14px' },
  lineHeight: { xs: '16px', sm: '20px' }
};

const featuresContainerSx: SxProps<Theme> = {
  marginTop: { xs: '16px' }
};

export const addToOrderButtonSx: SxProps<Theme> = {
  backgroundColor: 'var(--color-primary)',
  color: 'var(--color-white)',
  fontFamily: 'Inter',
  fontWeight: 500,
  fontSize: { xs: '14px', sm: '16px' },
  lineHeight: { xs: '20px', sm: '24px' },
  padding: { xs: '10px 20px', sm: '8px 18px' },
  borderRadius: '6px',
  textTransform: 'none',
  width: '100%',
  '&:hover': {
    backgroundColor: 'var(--color-primary-ultra)'
  }
};

// Sub-components
interface TagProps {
  label: string;
}

const Tag: React.FC<TagProps> = ({ label }) => (
  <Typography sx={tagSx}>{label}</Typography>
);

interface FeatureItemProps {
  text: string;
  isLast: boolean;
}

const FeatureItem: React.FC<FeatureItemProps> = ({ text, isLast }) => (
  <>
    <Box sx={CitationCampaignSx.featureItem}>
      <Box sx={CitationCampaignSx.checkmarkCircle}>
        <Box
          component="img"
          src={`${process.env.PUBLIC_URL}${CHECKMARK_ICON_PATH}`}
          alt="checkmark circle"
          width={16}
          height={16}
        />
      </Box>
      <Typography sx={CitationCampaignSx.featureText}>{text}</Typography>
    </Box>
    {!isLast && (
      <Divider
        orientation="horizontal"
        flexItem
        sx={CitationCampaignSx.divider}
      />
    )}
  </>
);

interface HeaderSectionProps {
  totalPrice: string;
  onAddToOrder: () => void;
  onRemove: () => void;
  isAddedToOrder: boolean;
}

const HeaderSection: React.FC<HeaderSectionProps> = ({
  totalPrice,
  onAddToOrder,
  onRemove,
  isAddedToOrder
}) => (
  <Box sx={headerContainerSx}>
    <Typography sx={titleSx}>Sync and Post for 12 months</Typography>
    <Box sx={headerActionsSx}>
      <Typography sx={totalPriceSx}>Total: {totalPrice}</Typography>
      {!isAddedToOrder && (
        <Button
          variant="contained"
          color="primary"
          onClick={onAddToOrder}
          sx={addToOrderButtonSx}
        >
          Add To Order
        </Button>
      )}
      {isAddedToOrder && (
        <Button
          variant="outlined"
          color="primary"
          onClick={onRemove}
          sx={removeAllButtonSx}
        >
          Remove
        </Button>
      )}
    </Box>
  </Box>
);

// Main Component
export const SyncAndPost: React.FC = () => {
  const { state: citationState, setSyncAndPost } = useCitation();

  const handleAddToOrder = () => {
    setSyncAndPost(true);
  };

  return (
    <Card>
      <Box sx={containerSx}>
        <HeaderSection
          totalPrice="$50.00"
          onAddToOrder={() => setSyncAndPost(true)}
          onRemove={() => setSyncAndPost(false)}
          isAddedToOrder={citationState.syncAndPost}
        />

        <Box>
          <Typography sx={descriptionSx}>
            Forget tedious manual updates! Sync and Post automates your business
            details and posts, saving you hours.
          </Typography>
        </Box>

        <Box sx={tagsContainerSx}>
          {TAGS.map((tag, index) => (
            <Tag key={index} label={tag} />
          ))}
        </Box>

        <Box sx={featuresContainerSx}>
          {FEATURES.map((feature, index) => (
            <FeatureItem
              key={index}
              text={feature}
              isLast={index === FEATURES.length - 1}
            />
          ))}
        </Box>
      </Box>
    </Card>
  );
};
